import React, { useState, useEffect, useCallback, memo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

// ==================== INDEPENDENT AMOUNT INPUT ====================
const IndependentAmountInput = memo(({ initialValue, onValueChange, disabled }) => {
  const [localValue, setLocalValue] = useState(initialValue || "");
  const isInternalChange = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalValue(initialValue || "");
    }
    isInternalChange.current = false;
  }, [initialValue]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      isInternalChange.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      onValueChange("");
      return;
    }
    
    if (/^\d*\.?\d{0,2}$/.test(newValue) && newValue.length <= 10) {
      setLocalValue(newValue);
      isInternalChange.current = true;
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onValueChange(newValue);
      }, 300);
    }
  };

  const handleClear = () => {
    setLocalValue("");
    isInternalChange.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onValueChange("");
  };

  return (
    <TextField
      fullWidth
      value={localValue}
      onChange={handleChange}
      onWheel={(e) => e.target.blur()}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }}
      placeholder="Enter amount limit"
      size="small"
      disabled={disabled}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <span style={{ color: '#666', fontWeight: 500 }}>₹</span>
          </InputAdornment>
        ),
        endAdornment: localValue && (
          <InputAdornment position="end">
            <IconButton onClick={handleClear} edge="end" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }}
      sx={{
        mb: 1,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          '&:hover': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78' } },
          '&.Mui-focused': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78', borderWidth: 2 } }
        }
      }}
    />
  );
});

// ==================== INDEPENDENT TRANSACTION INPUT ====================
const IndependentTransactionInput = memo(({ initialValue, onValueChange, disabled }) => {
  const [localValue, setLocalValue] = useState(initialValue || "");
  const isInternalChange = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalValue(initialValue || "");
    }
    isInternalChange.current = false;
  }, [initialValue]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      isInternalChange.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      onValueChange("");
      return;
    }
    
    if (/^\d*$/.test(newValue) && newValue.length <= 6) {
      setLocalValue(newValue);
      isInternalChange.current = true;
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onValueChange(newValue);
      }, 300);
    }
  };

  const handleClear = () => {
    setLocalValue("");
    isInternalChange.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onValueChange("");
  };

  return (
    <TextField
      fullWidth
      value={localValue}
      onChange={handleChange}
      onWheel={(e) => e.target.blur()}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }}
      placeholder="Enter transaction count"
      size="small"
      disabled={disabled}
      InputProps={{
        endAdornment: localValue && (
          <InputAdornment position="end">
            <IconButton onClick={handleClear} edge="end" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }}
      sx={{
        mb: 1,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          '&:hover': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78' } },
          '&.Mui-focused': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78', borderWidth: 2 } }
        }
      }}
    />
  );
});

// ==================== DURATION INPUT (NO LIMITS - USER CAN ENTER ANY DIGIT) ====================
const DurationInput = memo(({ value, onChange, unit, disabled }) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      return;
    }
    
    // Allow any positive integer (no upper limit)
    if (/^\d*$/.test(newValue)) {
      let numValue = parseInt(newValue, 10);
      if (!isNaN(numValue) && numValue > 0) {
        setLocalValue(numValue);
        
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          onChange(numValue);
        }, 300);
      } else if (newValue === "0") {
        // Don't allow zero, set to 1
        setLocalValue(1);
        onChange(1);
      }
    }
  };

  const handleBlur = () => {
    let numValue = parseInt(localValue, 10);
    if (isNaN(numValue) || numValue <= 0) {
      setLocalValue(1);
      onChange(1);
    } else {
      onChange(numValue);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <TextField
        type="number"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={(e) => e.target.blur()}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
          }
        }}
        size="small"
        disabled={disabled}
        inputProps={{
          min: 1,
          style: { textAlign: 'center' }
        }}
        sx={{
          width: 100,
          '& input[type=number]': {
            MozAppearance: 'textfield',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            }
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78' } },
            '&.Mui-focused': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78', borderWidth: 2 } }
          }
        }}
      />
      <Typography variant="caption" sx={{ color: "#666", minWidth: 40 }}>
        {unit}
      </Typography>
    </Box>
  );
});

// ==================== INDEPENDENT FREQUENCY BUTTONS ====================
const FrequencyButtons = memo(({ value, onChange, options, disabled }) => {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      {options.map((option) => (
        <Button
          key={option.value}
          size="small"
          variant={value === option.value ? "contained" : "outlined"}
          onClick={() => onChange(option.value)}
          disabled={disabled}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontSize: "12px",
            ...(value === option.value ? {
              background: "#14AE78",
              "&:hover": { background: "#0f8c65" }
            } : {})
          }}
        >
          {option.label}
        </Button>
      ))}
    </Box>
  );
});

// ==================== AMOUNT SECTION (FULLY INDEPENDENT) ====================
const AmountSection = memo(({ 
  initialValue,
  initialFrequency,
  initialDuration,
  onDataChange,
  loading
}) => {
  const [value, setValue] = useState(initialValue || "");
  const [frequency, setFrequency] = useState(initialFrequency || "DAYS");
  const [duration, setDuration] = useState(initialDuration || 7);
  
  const isInitialized = useRef(false);
  const timeoutRef = useRef(null);

  const frequencyOptions = [
    { value: "HOURS", label: "Hour" },
    { value: "DAYS", label: "Day" },
    { value: "MONTHS", label: "Month" },
  ];

  const getUnit = (freq) => {
    switch(freq) {
      case "HOURS": return "hrs";
      case "DAYS": return "days";
      case "MONTHS": return "months";
      default: return "days";
    }
  };

  const unit = getUnit(frequency);

  const sendDataToParent = useCallback((newValue, newFrequency, newDuration) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (newValue && parseFloat(newValue) > 0) {
        onDataChange({
          limitOn: "AMOUNT",
          value: parseFloat(newValue),
          frequency: newFrequency,
          duration: newDuration
        });
      } else if (!newValue || newValue === "") {
        onDataChange(null);
      }
    }, 300);
  }, [onDataChange]);

  const handleValueChange = useCallback((newValue) => {
    setValue(newValue);
    sendDataToParent(newValue, frequency, duration);
  }, [frequency, duration, sendDataToParent]);

  const handleFrequencyChange = useCallback((newFrequency) => {
    setFrequency(newFrequency);
    setDuration(1);
    sendDataToParent(value, newFrequency, 1);
  }, [value, sendDataToParent]);

  const handleDurationChange = useCallback((newDuration) => {
    setDuration(newDuration);
    sendDataToParent(value, frequency, newDuration);
  }, [value, frequency, sendDataToParent]);

  useEffect(() => {
    if (!isInitialized.current && initialValue) {
      setValue(initialValue);
      let uiFrequency = initialFrequency;
      if (uiFrequency === "MONTH") uiFrequency = "MONTHS";
      setFrequency(uiFrequency || "DAYS");
      setDuration(initialDuration || 1);
      isInitialized.current = true;
    }
  }, [initialValue, initialFrequency, initialDuration]);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32", mb: 2 }}>
        Amount Limit
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444" }}>
            Limit Value
          </Typography>
          <Typography variant="caption" sx={{ color: "#999" }}>
            {value.length}/20
          </Typography>
        </Box>
        <IndependentAmountInput
          initialValue={value}
          onValueChange={handleValueChange}
          disabled={loading}
        />
        <Typography variant="caption" sx={{ color: "#666" }}>
          Maximum amount (in ₹) per period
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Frequency
        </Typography>
        <FrequencyButtons
          value={frequency}
          onChange={handleFrequencyChange}
          options={frequencyOptions}
          disabled={loading}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Duration
        </Typography>
        <DurationInput
          value={duration}
          onChange={handleDurationChange}
          disabled={loading}
          unit={unit}
        />
      </Box>
    </Box>
  );
});

// ==================== TRANSACTION SECTION (FULLY INDEPENDENT) ====================
const TransactionSection = memo(({ 
  initialValue,
  initialFrequency,
  initialDuration,
  onDataChange,
  loading
}) => {
  const [value, setValue] = useState(initialValue || "");
  const [frequency, setFrequency] = useState(initialFrequency || "DAYS");
  const [duration, setDuration] = useState(initialDuration || 7);
  
  const isInitialized = useRef(false);
  const timeoutRef = useRef(null);

  const frequencyOptions = [
    { value: "HOURS", label: "Hour" },
    { value: "DAYS", label: "Day" },
    { value: "MONTHS", label: "Month" },
  ];

  const getUnit = (freq) => {
    switch(freq) {
      case "HOURS": return "hrs";
      case "DAYS": return "days";
      case "MONTHS": return "months";
      default: return "days";
    }
  };

  const unit = getUnit(frequency);

  const sendDataToParent = useCallback((newValue, newFrequency, newDuration) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (newValue && parseFloat(newValue) > 0) {
        onDataChange({
          limitOn: "TRANSACTION",
          value: parseInt(newValue, 10),
          frequency: newFrequency,
          duration: newDuration
        });
      } else if (!newValue || newValue === "") {
        onDataChange(null);
      }
    }, 300);
  }, [onDataChange]);

  const handleValueChange = useCallback((newValue) => {
    setValue(newValue);
    sendDataToParent(newValue, frequency, duration);
  }, [frequency, duration, sendDataToParent]);

  const handleFrequencyChange = useCallback((newFrequency) => {
    setFrequency(newFrequency);
    setDuration(1);
    sendDataToParent(value, newFrequency, 1);
  }, [value, sendDataToParent]);

  const handleDurationChange = useCallback((newDuration) => {
    setDuration(newDuration);
    sendDataToParent(value, frequency, newDuration);
  }, [value, frequency, sendDataToParent]);

  useEffect(() => {
    if (!isInitialized.current && initialValue) {
      setValue(initialValue);
      let uiFrequency = initialFrequency;
      if (uiFrequency === "MONTH") uiFrequency = "MONTHS";
      setFrequency(uiFrequency || "DAYS");
      setDuration(initialDuration || 1);
      isInitialized.current = true;
    }
  }, [initialValue, initialFrequency, initialDuration]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "#1565c0", mb: 2 }}>
        Transaction Limit
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444" }}>
            Transaction Count
          </Typography>
          <Typography variant="caption" sx={{ color: "#999" }}>
            {value.length}/20
          </Typography>
        </Box>
        <IndependentTransactionInput
          initialValue={value}
          onValueChange={handleValueChange}
          disabled={loading}
        />
        <Typography variant="caption" sx={{ color: "#666" }}>
          Maximum number of transactions per period
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Frequency
        </Typography>
        <FrequencyButtons
          value={frequency}
          onChange={handleFrequencyChange}
          options={frequencyOptions}
          disabled={loading}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Duration
        </Typography>
        <DurationInput
          value={duration}
          onChange={handleDurationChange}
          disabled={loading}
          unit={unit}
        />
      </Box>
    </Box>
  );
});

// ==================== MAIN UPDATE COMPONENT ====================
const UpdateUsageLimitComponent = ({
  usageLimitRequired,
  handleUsageLimitChange,
  onLimitsChange,
  initialLimits = [],
  loading,
}) => {
  const [showAmount, setShowAmount] = useState(false);
  const [showTransaction, setShowTransaction] = useState(false);
  
  const amountDataRef = useRef(null);
  const transactionDataRef = useRef(null);
  
  const sendCombinedLimits = useCallback(() => {
    const combinedLimits = [];
    
    if (amountDataRef.current) {
      combinedLimits.push(amountDataRef.current);
    }
    
    if (transactionDataRef.current) {
      combinedLimits.push(transactionDataRef.current);
    }
    
    if (onLimitsChange) {
      onLimitsChange(combinedLimits);
    }
  }, [onLimitsChange]);

  const handleAmountDataChange = useCallback((data) => {
    amountDataRef.current = data;
    sendCombinedLimits();
  }, [sendCombinedLimits]);

  const handleTransactionDataChange = useCallback((data) => {
    transactionDataRef.current = data;
    sendCombinedLimits();
  }, [sendCombinedLimits]);

  // Convert API frequency to UI format
  const convertToUIFrequency = useCallback((apiFrequency) => {
    if (!apiFrequency) return "DAYS";
    const upper = apiFrequency.toUpperCase();
    if (upper === "HOURS" || upper === "HOUR") return "HOURS";
    if (upper === "DAYS" || upper === "DAY") return "DAYS";
    if (upper === "MONTHS" || upper === "MONTH") return "MONTHS";
    return "DAYS";
  }, []);

  // Convert API limitOn to UI format
  const convertToUILimitOn = useCallback((apiLimitOn) => {
    if (!apiLimitOn) return "";
    const upper = apiLimitOn.toUpperCase();
    if (upper === "AMOUNT") return "AMOUNT";
    if (upper === "TRANSACTION") return "TRANSACTION";
    return "";
  }, []);

  // Initialize from existing limits
  useEffect(() => {
    if (initialLimits && initialLimits.length > 0) {
      initialLimits.forEach(limit => {
        const limitType = convertToUILimitOn(limit.limitOn);
        const frequency = convertToUIFrequency(limit.frequency);
        
        if (limitType === "AMOUNT") {
          setShowAmount(true);
          amountDataRef.current = {
            limitOn: "AMOUNT",
            value: limit.value,
            frequency: frequency,
            duration: limit.duration || 1
          };
        } else if (limitType === "TRANSACTION") {
          setShowTransaction(true);
          transactionDataRef.current = {
            limitOn: "TRANSACTION",
            value: limit.value,
            frequency: frequency,
            duration: limit.duration || 1
          };
        }
      });
    }
  }, [initialLimits, convertToUILimitOn, convertToUIFrequency]);

  const toggleAmount = useCallback(() => {
    setShowAmount(prev => !prev);
    if (showAmount) {
      amountDataRef.current = null;
      sendCombinedLimits();
    }
  }, [showAmount, sendCombinedLimits]);

  const toggleTransaction = useCallback(() => {
    setShowTransaction(prev => !prev);
    if (showTransaction) {
      transactionDataRef.current = null;
      sendCombinedLimits();
    }
  }, [showTransaction, sendCombinedLimits]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress size={32} />
        <Typography sx={{ ml: 2, color: '#666' }}>Loading usage limit data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: "auto" }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 1 }}>
        Usage Limit
      </Typography>
      <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
        Define spending thresholds and transaction volume controls for this corporate account.
      </Typography>

      <Box sx={{ background: "#f8f9fa", borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
          Do you want to set a usage limit?
        </Typography>

        <RadioGroup
          value={usageLimitRequired}
          onChange={(e) => handleUsageLimitChange(e.target.value)}
          row
          sx={{ gap: 3 }}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>Yes</Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                  Set a usage limit for corporate
                </Typography>
              </Box>
            }
            sx={{
              background: usageLimitRequired === "Yes" ? "#e8f5e9" : "transparent",
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0
            }}
          />
          <FormControlLabel
            value="No"
            control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>No</Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                  No usage limit required
                </Typography>
              </Box>
            }
            sx={{
              background: usageLimitRequired === "No" ? "#e8f5e9" : "transparent",
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0
            }}
          />
        </RadioGroup>
      </Box>

      {usageLimitRequired === "Yes" && (
        <Box sx={{ background: "white", borderRadius: 2, p: 3, border: "1px solid #e0e0e0", mt: 2 }}>
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
            What do you want to limit?
          </Typography>
          
          <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
            <Button
              variant={showAmount ? "contained" : "outlined"}
              onClick={toggleAmount}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                px: 4,
                py: 1,
                ...(showAmount ? {
                  background: "#14AE78",
                  color: "white",
                  "&:hover": { background: "#0f8c65" }
                } : {
                  borderColor: "#e0e0e0",
                  color: "#666",
                  "&:hover": { borderColor: "#14AE78", color: "#14AE78" }
                })
              }}
            >
              Amount
            </Button>
            <Button
              variant={showTransaction ? "contained" : "outlined"}
              onClick={toggleTransaction}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                px: 4,
                py: 1,
                ...(showTransaction ? {
                  background: "#14AE78",
                  color: "white",
                  "&:hover": { background: "#0f8c65" }
                } : {
                  borderColor: "#e0e0e0",
                  color: "#666",
                  "&:hover": { borderColor: "#14AE78", color: "#14AE78" }
                })
              }}
            >
              Transaction
            </Button>
          </Box>

          {showAmount && (
            <AmountSection
              initialValue={amountDataRef.current?.value?.toString() || ""}
              initialFrequency={amountDataRef.current?.frequency || "DAYS"}
              initialDuration={amountDataRef.current?.duration || 1}
              onDataChange={handleAmountDataChange}
              loading={loading}
            />
          )}

          {showTransaction && (
            <TransactionSection
              initialValue={transactionDataRef.current?.value?.toString() || ""}
              initialFrequency={transactionDataRef.current?.frequency || "DAYS"}
              initialDuration={transactionDataRef.current?.duration || 1}
              onDataChange={handleTransactionDataChange}
              loading={loading}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default UpdateUsageLimitComponent;